import React, { useMemo, useState } from 'react';
import { MapContainer, TileLayer, Marker, Popup, Polyline } from 'react-leaflet';
import MarkerClusterGroup from 'react-leaflet-cluster';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { Map, Package } from 'lucide-react';

// Map layer configurations - URLs are generated dynamically to ensure env var is loaded
const getMapLayers = () => {
  const apiKey = process.env.REACT_APP_MAPTILER_API_KEY;
  
  if (!apiKey) {
    console.warn('MapTiler API key not found. Satellite/Hybrid views may not work. Check REACT_APP_MAPTILER_API_KEY in .env');
  }
  
  return {
    street: {
      url: 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png',
      attribution: '&copy; OpenStreetMap contributors',
      name: 'Street'
    },
    satellite: {
      url: `https://api.maptiler.com/maps/satellite/{z}/{x}/{y}.jpg?key=${apiKey || ''}`,
      attribution: '&copy; <a href="https://www.maptiler.com/copyright/" target="_blank">MapTiler</a> &copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
      name: 'Satellite'
    },
    hybrid: {
      url: `https://api.maptiler.com/maps/hybrid/{z}/{x}/{y}.jpg?key=${apiKey || ''}`,
      attribution: '&copy; <a href="https://www.maptiler.com/copyright/" target="_blank">MapTiler</a> &copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
      name: 'Hybrid'
    }
  };
};

// Create vehicle marker with heading direction and speed state
// Shows directional arrow when moving, parked indicator when stationary
const createVehicleIcon = (heading = 0, speed = null, status = 'in_transit') => {
  // If speed is null/undefined, we don't have telematics data - show as "unknown" state
  // Use status to infer movement if speed data isn't available
  const hasSpeedData = speed !== null && speed !== undefined;
  const inferredMoving = !hasSpeedData && status && status.toLowerCase().includes('transit');
  
  const isParked = hasSpeedData ? speed < 2 : (!inferredMoving);
  const isMoving = hasSpeedData ? speed >= 2 : inferredMoving;
  const isFast = hasSpeedData && speed >= 55; // Highway speed
  
  // Color scheme based on state
  const movingColor = isFast ? '#F59E0B' : '#10B981'; // amber for highway, emerald for normal
  const parkedColor = '#6B7280'; // gray-500
  const unknownColor = '#3B82F6'; // blue when no speed data but assumed moving
  
  const mainColor = isParked ? parkedColor : (hasSpeedData ? movingColor : unknownColor);
  const glowColor = isParked ? 'rgba(107, 114, 128, 0.3)' : (isFast ? 'rgba(245, 158, 11, 0.4)' : 'rgba(16, 185, 129, 0.4)');
  
  const size = 40;
  const innerSize = 28;
  
  // Create the SVG with proper rotation and styling
  const svg = isParked ? `
    <!-- Parked Vehicle Marker -->
    <div style="position: relative; width: ${size}px; height: ${size}px;">
      <!-- Outer glow ring -->
      <div style="
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        width: ${size}px;
        height: ${size}px;
        background: ${glowColor};
        border-radius: 50%;
        animation: parked-pulse 3s ease-in-out infinite;
      "></div>
      <!-- Main circle -->
      <div style="
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        width: ${innerSize}px;
        height: ${innerSize}px;
        background: linear-gradient(145deg, #4B5563, #374151);
        border: 2.5px solid white;
        border-radius: 50%;
        box-shadow: 0 3px 10px rgba(0,0,0,0.25);
        display: flex;
        align-items: center;
        justify-content: center;
      ">
        <!-- P indicator for parked -->
        <span style="
          color: white;
          font-size: 12px;
          font-weight: 700;
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
          text-shadow: 0 1px 2px rgba(0,0,0,0.3);
        ">P</span>
      </div>
    </div>
    <style>
      @keyframes parked-pulse {
        0%, 100% { opacity: 0.6; transform: translate(-50%, -50%) scale(1); }
        50% { opacity: 0.3; transform: translate(-50%, -50%) scale(1.1); }
      }
    </style>
  ` : `
    <!-- Moving Vehicle Marker -->
    <div style="position: relative; width: ${size}px; height: ${size}px;">
      <!-- Motion trail effect -->
      ${isMoving ? `
        <div style="
          position: absolute;
          top: 50%;
          left: 50%;
          width: ${size + 6}px;
          height: ${size + 6}px;
          transform: translate(-50%, -50%) rotate(${heading}deg);
          pointer-events: none;
        ">
          <div style="
            position: absolute;
            bottom: -2px;
            left: 50%;
            transform: translateX(-50%);
            width: 2px;
            height: 10px;
            background: linear-gradient(to top, transparent, ${mainColor}40);
            border-radius: 2px;
          "></div>
        </div>
      ` : ''}
      
      <!-- Animated glow ring -->
      <div style="
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        width: ${size}px;
        height: ${size}px;
        background: ${glowColor};
        border-radius: 50%;
        animation: ${isFast ? 'fast-pulse' : 'moving-pulse'} ${isFast ? '0.8s' : '2s'} ease-in-out infinite;
      "></div>
      
      <!-- Main directional arrow container -->
      <div style="
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        width: ${innerSize}px;
        height: ${innerSize}px;
      ">
        <svg 
          width="${innerSize}" 
          height="${innerSize}" 
          viewBox="0 0 32 32" 
          style="
            transform: rotate(${heading}deg);
            filter: drop-shadow(0 2px 5px rgba(0,0,0,0.25));
          "
        >
          <defs>
            <linearGradient id="vehicleGrad${Math.random().toString(36).substr(2, 9)}" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" style="stop-color:${mainColor};stop-opacity:1" />
              <stop offset="100%" style="stop-color:${mainColor};stop-opacity:0.8" />
            </linearGradient>
          </defs>
          <circle cx="16" cy="16" r="13" fill="${mainColor}" stroke="white" stroke-width="2"/>
          <path d="M16 7 L21 17 L16 13 L11 17 Z" fill="white" stroke="white" stroke-width="0.5" stroke-linejoin="round"/>
        </svg>
      </div>
      
      <!-- Speed badge -->
      <div style="
        position: absolute;
        bottom: -2px;
        right: -2px;
        min-width: 18px;
        height: 14px;
        padding: 0 3px;
        background: ${isFast ? '#F59E0B' : mainColor};
        border: 1.5px solid white;
        border-radius: 7px;
        box-shadow: 0 1px 3px rgba(0,0,0,0.2);
        display: flex;
        align-items: center;
        justify-content: center;
      ">
        <span style="
          color: white;
          font-size: 8px;
          font-weight: 700;
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
          line-height: 1;
        ">${hasSpeedData ? Math.round(speed) : '—'}</span>
      </div>
    </div>
    <style>
      @keyframes moving-pulse {
        0%, 100% { opacity: 0.8; transform: translate(-50%, -50%) scale(1); }
        50% { opacity: 0.4; transform: translate(-50%, -50%) scale(1.15); }
      }
      @keyframes fast-pulse {
        0%, 100% { opacity: 0.9; transform: translate(-50%, -50%) scale(1); }
        50% { opacity: 0.5; transform: translate(-50%, -50%) scale(1.2); }
      }
    </style>
  `;
  
  return L.divIcon({
    html: svg,
    className: 'vehicle-marker',
    iconSize: [size, size],
    iconAnchor: [size / 2, size / 2],
    popupAnchor: [0, -size / 2]
  });
};

// Layer Toggle Control Component - Sleek floating button design
const LayerToggleControl = ({ activeLayer, onLayerChange }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  
  const layers = [
    { id: 'street', icon: '🗺️', label: 'Street' },
    { id: 'satellite', icon: '🛰️', label: 'Satellite' },
    { id: 'hybrid', icon: '🌍', label: 'Hybrid' }
  ];
  
  const activeLayerData = layers.find(l => l.id === activeLayer);
  
  return (
    <div className="absolute top-3 right-3 z-[1000]">
      <div 
        className={`
          bg-white/95 backdrop-blur-md rounded-xl shadow-lg border border-gray-200/50
          transition-all duration-300 ease-out overflow-hidden
          ${isExpanded ? 'w-36' : 'w-auto'}
        `}
        onMouseEnter={() => setIsExpanded(true)}
        onMouseLeave={() => setIsExpanded(false)}
      >
        {/* Collapsed state - shows current layer */}
        <div 
          className={`
            flex items-center gap-2 px-2.5 py-2 cursor-pointer
            ${isExpanded ? 'border-b border-gray-100' : ''}
          `}
          onClick={() => setIsExpanded(!isExpanded)}
        >
          <div className="w-6 h-6 rounded-md bg-gradient-to-br from-slate-700 to-slate-900 flex items-center justify-center shadow-sm">
            <svg xmlns="http://www.w3.org/2000/svg" className="w-3.5 h-3.5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
            </svg>
          </div>
          <div className={`flex flex-col transition-opacity duration-200 ${isExpanded ? 'opacity-100' : 'opacity-0 w-0'}`}>
            <span className="text-[9px] font-medium text-gray-400 uppercase tracking-wide">Map</span>
            <span className="text-[11px] font-semibold text-gray-700">{activeLayerData?.label}</span>
          </div>
          {isExpanded && (
            <svg xmlns="http://www.w3.org/2000/svg" className="w-3 h-3 text-gray-400 ml-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
            </svg>
          )}
        </div>
        
        {/* Expanded layer options */}
        <div className={`
          transition-all duration-300 ease-out
          ${isExpanded ? 'max-h-36 opacity-100' : 'max-h-0 opacity-0'}
        `}>
          {layers.map((layer) => (
            <button
              key={layer.id}
              onClick={() => {
                onLayerChange(layer.id);
                setIsExpanded(false);
              }}
              className={`
                w-full flex items-center gap-2.5 px-2.5 py-1.5 text-left transition-all duration-150
                ${activeLayer === layer.id 
                  ? 'bg-gradient-to-r from-blue-50 to-indigo-50 text-blue-700' 
                  : 'hover:bg-gray-50 text-gray-600 hover:text-gray-900'
                }
              `}
            >
              <span className="text-sm">{layer.icon}</span>
              <span className={`text-xs ${activeLayer === layer.id ? 'font-semibold' : 'font-medium'}`}>
                {layer.label}
              </span>
              {activeLayer === layer.id && (
                <svg xmlns="http://www.w3.org/2000/svg" className="w-3.5 h-3.5 ml-auto text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                </svg>
              )}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

const MapComponent = ({ locations, clustered = true, showInfo = true, isLoadStop = false, isCarrier = false, isUserLocation = false }) => {
  // Add check for window object
  const [isClient, setIsClient] = React.useState(false);
  const [activeLayer, setActiveLayer] = useState('street');
  
  // Get map layers configuration (memoized)
  const MAP_LAYERS = useMemo(() => getMapLayers(), []);

  React.useEffect(() => {
    setIsClient(true);
  }, []);

  // Create markers for different user types - Move inside useEffect
  const [markerIcons, setMarkerIcons] = React.useState(null);
  const [stopMarkerIcons, setStopMarkerIcons] = React.useState(null);

  React.useEffect(() => {
    if (typeof window !== 'undefined') {
      setMarkerIcons({
        authenticated: L.icon({
          iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-blue.png',
          shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/0.7.7/images/marker-shadow.png',
          iconSize: [25, 41],
          iconAnchor: [12, 41],
          popupAnchor: [1, -34],
          shadowSize: [41, 41]
        }),
        anonymous: L.icon({
          iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-grey.png',
          shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/0.7.7/images/marker-shadow.png',
          iconSize: [20, 32],
          iconAnchor: [10, 32],
          popupAnchor: [1, -34],
          shadowSize: [41, 41]
        })
      });

      setStopMarkerIcons({
        pickup: L.icon({
          iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-green.png',
          shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/0.7.7/images/marker-shadow.png',
          iconSize: [25, 41],
          iconAnchor: [12, 41],
          popupAnchor: [1, -34],
          shadowSize: [41, 41]
        }),
        delivery: L.icon({
          iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-red.png',
          shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/0.7.7/images/marker-shadow.png',
          iconSize: [25, 41],
          iconAnchor: [12, 41],
          popupAnchor: [1, -34],
          shadowSize: [41, 41]
        }),
        current: L.icon({
          iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-gold.png',
          shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/0.7.7/images/marker-shadow.png',
          iconSize: [30, 45],
          iconAnchor: [15, 45],
          popupAnchor: [1, -34],
          shadowSize: [41, 41]
        })
      });
    }
  }, []);

  // Calculate bounds from all location points
  const bounds = useMemo(() => {
    if (!locations?.length) return null;
    
    const points = locations
      .filter(loc => loc.location?.latitude && loc.location?.longitude)
      .map(loc => [parseFloat(loc.location.latitude), parseFloat(loc.location.longitude)]);
    
    return points.length > 0 ? L.latLngBounds(points) : null;
  }, [locations]);

  // Default center of USA if no bounds
  const defaultCenter = [39.8283, -98.5795];
  const defaultZoom = 4;

  // Create a memoized array of coordinates for the route line
  const routeCoordinates = useMemo(() => {
    if (!isLoadStop || !locations?.length) return [];
    
    return locations
      .filter(loc => loc.location?.latitude && loc.location?.longitude)
      .sort((a, b) => a.sequence - b.sequence)
      .map(loc => [
        parseFloat(loc.location.latitude),
        parseFloat(loc.location.longitude)
      ]);
  }, [locations, isLoadStop]);

  // Define route line styles
  const routeStyle = {
    color: '#4f46e5', // Indigo-600
    weight: 3,
    opacity: 0.7,
    dashArray: '10, 10', // Creates a dashed line
    lineJoin: 'round'
  };

  const renderMarkers = () => {
    if (!locations?.length || !markerIcons || !stopMarkerIcons) return null;

    const getStopIcon = (loc) => {
      // If this is a current location marker with heading/speed data, use directional vehicle icon
      if (loc.is_current || loc.stop_type === 'current') {
        const speedValue = loc.speed !== undefined ? loc.speed : null;
        return createVehicleIcon(
          loc.heading || 0,
          speedValue,
          loc.status
        );
      }
      
      // Handle standard stop types
      switch (loc.stop_type?.toLowerCase()) {
        case 'pickup':
          return stopMarkerIcons.pickup;
        case 'delivery':
          return stopMarkerIcons.delivery;
        case 'stop':
          // Legacy case - use delivery icon for consistency
          return stopMarkerIcons.delivery;
        default:
          return stopMarkerIcons.delivery;
      }
    };

    const markers = locations.map((loc, index) => {
      if (!loc.location?.latitude || !loc.location?.longitude) return null;

      // For current locations (vehicles), use dynamic vehicle icon; otherwise use standard icons
      let icon;
      if (loc.is_current || loc.stop_type === 'current') {
        // Pass speed as null if not defined, so we can distinguish "no data" from "speed is 0"
        const speedValue = loc.speed !== undefined ? loc.speed : null;
        icon = createVehicleIcon(loc.heading || 0, speedValue, loc.status);
      } else if (isLoadStop) {
        icon = getStopIcon(loc);
      } else {
        icon = markerIcons[loc.is_authenticated ? 'authenticated' : 'anonymous'];
      }

      return (
        <Marker
          key={`location-${loc.id || index}`}
          position={[loc.location.latitude, loc.location.longitude]}
          icon={icon}
        >
          {showInfo && (
            <Popup>
              <div className="p-2">
                {isLoadStop ? (
                  <>
                    <h3 className="font-semibold capitalize">
                      {loc.is_current ? 'Current Location' : `${loc.stop_type} Stop #${loc.sequence + 1}`}
                    </h3>
                    {isCarrier ? (
                      <p className="text-sm text-gray-600">
                        {loc.location.city}, {loc.location.state}
                      </p>
                    ) : (
                      <>
                        <p className="text-sm text-gray-600">
                          {loc.location.address}<br />
                          {loc.location.city}, {loc.location.state}
                        </p>
                        <div className="mt-2">
                          {loc.business_name && (
                            <p className="text-sm font-medium">
                              {loc.business_name}
                            </p>
                          )}
                        </div>
                      </>
                    )}
                    <div className="mt-2">
                      <p className="text-sm text-gray-600">
                        {loc.timestamp}
                      </p>
                      {!isCarrier && (
                        <div className="mt-2 space-y-1">
                          {loc.contact_name && (
                            <p className="text-sm">Contact: {loc.contact_name}</p>
                          )}
                          {loc.contact_phone && (
                            <p className="text-sm">Phone: {loc.contact_phone}</p>
                          )}
                          {loc.contact_instructions && (
                            <p className="text-sm text-gray-600 mt-2">
                              {loc.contact_instructions}
                            </p>
                          )}
                        </div>
                      )}
                    </div>
                  </>
                ) : isUserLocation ? (
                  <>
                    <h3 className="font-semibold">
                      User Location
                    </h3>
                    <div className="mt-1 text-sm">
                      {loc.user && (
                        <p className="text-gray-700">
                          <span className="font-medium">User:</span> {loc.user.name || loc.user.email}
                        </p>
                      )}
                      <p className="text-gray-700">
                        <span className="font-medium">Location:</span> {loc.location.city || ''}, {loc.location.state || ''} {loc.location.country_code ? `(${loc.location.country_code})` : ''}
                      </p>
                      {loc.network && (
                        <p className="text-gray-700">
                          <span className="font-medium">Network:</span> {loc.network.isp || 'Unknown'} 
                          {loc.network.is_proxy && <span className="text-red-600 ml-1">(Proxy detected)</span>}
                        </p>
                      )}
                      {loc.device && (
                        <p className="text-gray-700">
                          <span className="font-medium">Device:</span> {loc.device.device_type || 'Unknown'} 
                          {loc.device.is_mobile && <span className="ml-1">(Mobile)</span>}
                        </p>
                      )}
                      <p className="text-gray-500 text-xs mt-1">
                        Timestamp: {loc.timestamp}
                      </p>
                    </div>
                  </>
                ) : (
                  <div className="min-w-[200px]">
                    {/* Status Header */}
                    <div className="flex items-center gap-2 mb-2">
                      {(() => {
                        const hasSpeed = loc.speed != null && loc.speed !== undefined;
                        const isParked = hasSpeed ? loc.speed < 2 : false;
                        const isMoving = hasSpeed ? loc.speed >= 2 : loc.status?.toLowerCase().includes('transit');
                        
                        if (isParked) {
                          return (
                            <div className="w-7 h-7 rounded-lg bg-gray-100 flex items-center justify-center">
                              <span className="text-gray-600 text-xs font-bold">P</span>
                            </div>
                          );
                        } else {
                          return (
                            <div className={`w-7 h-7 rounded-lg flex items-center justify-center ${
                              hasSpeed && loc.speed >= 55 ? 'bg-amber-100' : 'bg-emerald-100'
                            }`}>
                              <svg className={`w-3.5 h-3.5 ${
                                hasSpeed && loc.speed >= 55 ? 'text-amber-600' : 'text-emerald-600'
                              }`} 
                                   style={{ transform: `rotate(${loc.heading || 0}deg)` }}
                                   fill="currentColor" viewBox="0 0 24 24">
                                <path d="M12 2L4 14h6v8l8-12h-6z"/>
                              </svg>
                            </div>
                          );
                        }
                      })()}
                      <div>
                        <h3 className="font-semibold text-gray-800">
                          Load {loc.load_display_id || loc.load_id}
                        </h3>
                        <p className="text-[10px] text-gray-500">
                          {(() => {
                            const hasSpeed = loc.speed != null && loc.speed !== undefined;
                            if (hasSpeed) {
                              return loc.speed < 2 ? 'Parked' : (loc.speed >= 55 ? 'Highway Speed' : 'In Transit');
                            }
                            return loc.status?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) || 'Active';
                          })()}
                        </p>
                      </div>
                    </div>
                    
                    {/* Speed & Heading (if available) */}
                    <div className="grid grid-cols-2 gap-1.5 mb-2">
                      <div className={`p-1.5 rounded ${
                        loc.speed != null && loc.speed >= 55 ? 'bg-amber-50' : 'bg-gray-50'
                      }`}>
                        <p className="text-[9px] uppercase text-gray-500 font-medium">Speed</p>
                        <p className={`text-sm font-bold ${
                          loc.speed != null && loc.speed >= 55 ? 'text-amber-600' : 
                          loc.speed != null && loc.speed < 2 ? 'text-gray-400' : 'text-gray-700'
                        }`}>
                          {loc.speed != null ? `${Math.round(loc.speed)} mph` : '—'}
                        </p>
                      </div>
                      <div className="p-1.5 rounded bg-gray-50">
                        <p className="text-[9px] uppercase text-gray-500 font-medium">Heading</p>
                        <p className="text-sm font-bold text-gray-700">
                          {loc.heading != null ? `${Math.round(loc.heading)}°` : '—'}
                        </p>
                      </div>
                    </div>
                    
                    {/* Route Info */}
                    <div className="text-xs space-y-1 border-t border-gray-100 pt-2">
                      <p className="flex justify-between">
                        <span className="text-gray-500">Route</span>
                        <span className="font-medium text-gray-700 text-right">
                          {loc.origin_city}, {loc.origin_state} → {loc.destination_city}, {loc.destination_state}
                        </span>
                      </p>
                      <p className="flex justify-between">
                        <span className="text-gray-500">Driver</span>
                        <span className="font-medium text-gray-700">{loc.contact_name}</span>
                      </p>
                      <p className="flex justify-between">
                        <span className="text-gray-500">Status</span>
                        <span className="font-medium text-gray-700">
                          {loc.status?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                        </span>
                      </p>
                    </div>
                    
                    <p className="text-[10px] text-gray-400 mt-2 text-center">
                      Updated: {loc.timestamp}
                    </p>
                  </div>
                )}
              </div>
            </Popup>
          )}
        </Marker>
      );
    });

    return clustered ? (
      <MarkerClusterGroup chunkedLoading>
        {markers}
      </MarkerClusterGroup>
    ) : markers;
  };

  // Add conditional render for the entire map
  if (!isClient) {
    return <div className="h-full w-full rounded-lg bg-gray-100" />;
  }

  return (
    <div className="relative h-full w-full">
      <MapContainer
        key={isClient ? 'client' : 'server'} // Add key to force remount
        bounds={bounds}
        center={!bounds ? defaultCenter : undefined}
        zoom={!bounds ? defaultZoom : undefined}
        className="h-full w-full rounded-lg z-0"
        zoomControl={true}
      >
        <TileLayer
          key={activeLayer}
          url={MAP_LAYERS[activeLayer].url}
          attribution={MAP_LAYERS[activeLayer].attribution}
          maxZoom={activeLayer === 'street' ? 19 : 20}
          tileSize={activeLayer === 'street' ? 256 : 512}
          zoomOffset={activeLayer === 'street' ? 0 : -1}
        />
        
        {/* Add route line */}
        {isLoadStop && routeCoordinates.length > 1 && (
          <Polyline 
            positions={routeCoordinates}
            pathOptions={routeStyle}
          />
        )}
        
        {renderMarkers()}
      </MapContainer>
      
      {/* Layer Toggle Control */}
      <LayerToggleControl 
        activeLayer={activeLayer} 
        onLayerChange={setActiveLayer} 
      />
    </div>
  );
};

export default MapComponent; 